import { useEffect, useMemo, useState } from "react";
import { useParams, NavLink } from "react-router";
import { motion } from "motion/react";
import {
  ArrowLeft,
  MapPin,
  Globe,
  Users,
  CalendarDays,
  BookOpen,
  GraduationCap,
  CheckCircle2,
  MessageCircle,
  ArrowRight,
  Building2,
  ExternalLink,
  TrendingUp,
} from "lucide-react";
import logo from "../../assets/logo/logo.jpeg";
import { University } from "../../types/University";
import { db } from "../../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import { Navbar } from "../../Components/Navbar";

type UniversityListing = University & {
  website?: string;
  scholarship?: unknown;
};

const accentColor = "#7C3AED";
const bgGradient = "from-violet-600 to-purple-500";

function getInitials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
}

function getLogoUrl(logo?: string) {
  if (!logo) return null;

  try {
    return new URL(`../../assets/images/${logo}`, import.meta.url).href;
  } catch {
    return null;
  }
}

function cleanList(items?: string[]) {
  return (items ?? [])
    .map((item) => String(item || "").trim())
    .filter((item) => item && item.toLowerCase() !== "none");
}

function parseKeyInfo(items: string[], label: string) {
  const match = items.find((item) =>
    item.toLowerCase().startsWith(label.toLowerCase()),
  );

  if (!match) return undefined;

  const [, value] = match.split(/:(.*)/s);
  return value?.trim() || match.trim();
}

function splitStrength(item: string) {
  const colonIndex = item.indexOf(":");
  if (colonIndex === -1) return { title: item, points: [] };

  return {
    title: item.slice(0, colonIndex).trim(),
    points: item
      .slice(colonIndex + 1)
      .split(";")
      .map((point) => point.trim())
      .filter(Boolean),
  };
}

function getCampusLocation(campus: unknown) {
  if (typeof campus === "string") return campus.trim();

  if (campus && typeof campus === "object") {
    const campusRecord = campus as Record<string, unknown>;

    if (typeof campusRecord.location === "string") {
      return campusRecord.location.trim();
    }

    if (typeof campusRecord.campus === "string") {
      return campusRecord.campus.trim();
    }
  }

  return "";
}

function normalizeCampus(campus: unknown): University["campus"] {
  if (!Array.isArray(campus)) return [];

  return campus
    .map((item) => {
      const location = getCampusLocation(item);

      if (!location) return null;

      return {
        iframe:
          item && typeof item === "object" && "iframe" in item
            ? String((item as { iframe?: unknown }).iframe || "")
            : "",
        location,
      };
    })
    .filter((item): item is University["campus"][number] => item !== null);
}

export default function UniversityDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [uni, setUni] = useState<UniversityListing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUniversity = async () => {
      if (!slug) {
        setUni(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch from Firestore instead of Typesense
        const snapshot = await getDocs(
          query(
            collection(db, "university-listing"),
            where("slug", "==", slug),
          ),
        );

        const doc = snapshot.docs[0];
        if (!doc) {
          setUni(null);
          return;
        }

        const document = { id: doc.id, ...doc.data() } as UniversityListing;
        const firestoreCampus = normalizeCampus(document.campus);

        setUni({
          ...document,
          campus: firestoreCampus.length > 0 ? firestoreCampus : [],
        });
      } catch (err) {
        console.error("Failed to load university:", err);
        setError("Failed to load university details.");
        setUni(null);
      } finally {
        setLoading(false);
      }
    };

    fetchUniversity();
  }, [slug]);

  const derived = useMemo(() => {
    const keyInfo = cleanList(uni?.keyInfo);
    const rankings = cleanList(uni?.rankings);
    const foundationIntake = cleanList(uni?.foundationIntake);
    const diplomaIntake = cleanList(uni?.diplomaIntake);
    const degreeIntake = cleanList(uni?.degreeIntake);
    const masterIntake = cleanList(uni?.masterIntake);
    const intakes = [
      ...foundationIntake,
      ...diplomaIntake,
      ...degreeIntake,
      ...masterIntake,
    ];
    const campus = normalizeCampus(uni?.campus);

    return {
      keyInfo,
      rankings,
      foundationIntake,
      diplomaIntake,
      degreeIntake,
      masterIntake,
      intakes,
      courses: cleanList(uni?.courses),
      strengths: cleanList(uni?.strengthsHighlights),
      campus,
      logoUrl: getLogoUrl(uni?.logo),
      rankingDisplay: rankings[0] ?? "SCS Education partner",
      established: parseKeyInfo(keyInfo, "Year Established") ?? "-",
      students: parseKeyInfo(keyInfo, "Student Population") ?? "-",
      international:
        parseKeyInfo(keyInfo, "International Students") ?? uni?.country ?? "-",
      location:
        campus
          .map((item) => item.location)
          .filter(Boolean)
          .join("; ") ||
        uni?.country ||
        "-",
      intakeDisplay: intakes.slice(0, 3).join(", ") || "-",
    };
  }, [uni]);

  if (loading) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-500 text-[15px]">Loading university...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <Navbar />
        <p className="text-red-500 text-[15px]">{error}</p>
        <NavLink
          to="/explore-universities"
          className="mt-4 inline-flex items-center gap-2 text-violet-600 text-[13px]"
          style={{ fontWeight: 600 }}
        >
          <ArrowLeft className="w-4 h-4" /> Back to Universities Page
        </NavLink>
      </div>
    );
  }

  if (!uni) {
    return (
      <div className="p-8 text-center">
        <Navbar />
        <p className="text-slate-500 text-[15px]">University not found.</p>
        <NavLink
          to="/explore-universities"
          className="mt-4 inline-flex items-center gap-2 text-violet-600 text-[13px]"
          style={{ fontWeight: 600 }}
        >
          <ArrowLeft className="w-4 h-4" /> Back to Universities Page
        </NavLink>
      </div>
    );
  }

  const name = uni.universityName;

  return (
    <div className="max-w-4xl mx-auto pb-16">
      <Navbar />
      <div className="px-4 lg:px-8 pt-6 pb-2 mt-16">
        <NavLink
          to="/explore-universities"
          className="inline-flex items-center gap-2 text-[13px] text-slate-500 hover:text-slate-700 transition-colors"
          style={{ fontWeight: 500 }}
        >
          <ArrowLeft className="w-4 h-4" /> Back to Universities Page
        </NavLink>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mx-4 lg:mx-8 rounded-3xl overflow-hidden shadow-xl shadow-slate-200/60 mb-6"
      >
        <div className={`bg-gradient-to-br ${bgGradient} p-7 pb-10 relative`}>
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white/10 -translate-y-1/3 translate-x-1/4" />
          <div className="absolute bottom-0 left-8 w-32 h-32 rounded-full bg-white/5 translate-y-1/2" />

          <div className="relative flex items-start gap-5">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center text-white text-[18px] flex-shrink-0 shadow-lg overflow-hidden">
              {derived.logoUrl ? (
                <img
                  src={derived.logoUrl}
                  alt={name}
                  className="w-full h-full object-contain bg-white p-2"
                />
              ) : (
                <span style={{ fontWeight: 900 }}>{getInitials(name)}</span>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span
                  className="text-[11px] text-white/70 uppercase tracking-widest"
                  style={{ fontWeight: 600 }}
                >
                  {uni.country}
                </span>
              </div>
              <h1
                className="text-[22px] lg:text-[28px] text-white leading-tight mb-1"
                style={{ fontWeight: 900 }}
              >
                {name}
              </h1>
              <div className="flex items-center gap-1.5 mb-3">
                <MapPin className="w-3.5 h-3.5 text-white/70" />
                <span
                  className="text-[12px] text-white/80"
                  style={{ fontWeight: 400 }}
                >
                  {derived.location}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm">
                  <TrendingUp className="w-3 h-3 text-white" />
                  <span
                    className="text-[11px] text-white"
                    style={{ fontWeight: 600 }}
                  >
                    {derived.rankingDisplay}
                  </span>
                </div>
                <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm">
                  <Globe className="w-3 h-3 text-white" />
                  <span
                    className="text-[11px] text-white"
                    style={{ fontWeight: 600 }}
                  >
                    {uni.country}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white grid grid-cols-2 sm:grid-cols-4 divide-x divide-slate-100 border-t border-slate-100">
          {[
            {
              icon: <CalendarDays className="w-4 h-4" />,
              label: "Est.",
              value: derived.established,
            },
            {
              icon: <Users className="w-4 h-4" />,
              label: "Students",
              value: derived.students,
            },
            {
              icon: <Globe className="w-4 h-4" />,
              label: "International",
              value: derived.international,
            },
            {
              icon: <CalendarDays className="w-4 h-4" />,
              label: "Intakes",
              value: derived.intakeDisplay,
            },
          ].map((stat, i) => (
            <div key={i} className="flex flex-col items-center py-4 gap-1 px-3">
              <div className="text-slate-400">{stat.icon}</div>
              <p
                className="text-[13px] text-slate-900 text-center"
                style={{ fontWeight: 700 }}
              >
                {stat.value}
              </p>
              <p
                className="text-[10px] text-slate-400 uppercase tracking-wider"
                style={{ fontWeight: 600 }}
              >
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </motion.div>

      <div className="px-4 lg:px-8 flex flex-col gap-6">
        {uni.intro && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-4 h-4" style={{ color: accentColor }} />
              <h2
                className="text-[15px] text-slate-900"
                style={{ fontWeight: 700 }}
              >
                About
              </h2>
            </div>
            <p
              className="text-[13px] text-slate-600 leading-relaxed whitespace-pre-line"
              style={{ fontWeight: 400 }}
            >
              {uni.intro}
            </p>
          </motion.div>
        )}

        {(derived.keyInfo.length > 0 || derived.rankings.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <GraduationCap
                className="w-4 h-4"
                style={{ color: accentColor }}
              />
              <h2
                className="text-[15px] text-slate-900"
                style={{ fontWeight: 700 }}
              >
                Key Information
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[...derived.rankings, ...derived.keyInfo].map((item, i) => (
                <div
                  key={`${item}-${i}`}
                  className="flex items-start gap-2.5 p-3 rounded-xl bg-slate-50 border border-slate-100"
                >
                  <CheckCircle2
                    className="w-4 h-4 mt-0.5 flex-shrink-0"
                    style={{ color: accentColor }}
                  />
                  <span
                    className="text-[12px] text-slate-700"
                    style={{ fontWeight: 500 }}
                  >
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {derived.intakes.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6"
          >
            <div className="flex items-center gap-2 mb-5">
              <CalendarDays
                className="w-4 h-4"
                style={{ color: accentColor }}
              />
              <h2
                className="text-[15px] text-slate-900"
                style={{ fontWeight: 700 }}
              >
                Upcoming Intakes
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                ["Foundation", derived.foundationIntake],
                ["Diploma", derived.diplomaIntake],
                ["Degree", derived.degreeIntake],
                ["Master", derived.masterIntake],
              ]
                .filter(([, items]) => (items as string[]).length > 0)
                .map(([level, items]) => (
                  <div
                    key={level as string}
                    className="p-4 rounded-2xl border border-slate-100 bg-slate-50/50"
                  >
                    <p
                      className="text-[13px] text-slate-900 mb-2"
                      style={{ fontWeight: 700 }}
                    >
                      {level as string}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(items as string[]).map((intake) => (
                        <span
                          key={intake}
                          className="text-[10px] px-2 py-0.5 rounded-full bg-white text-slate-600 border border-slate-100"
                          style={{ fontWeight: 600 }}
                        >
                          {intake}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          </motion.div>
        )}

        {derived.strengths.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="rounded-3xl p-6"
            style={{
              background: `linear-gradient(135deg, ${accentColor}12 0%, ${accentColor}06 100%)`,
              border: `1px solid ${accentColor}22`,
            }}
          >
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4" style={{ color: accentColor }} />
              <h2
                className="text-[15px] text-slate-900"
                style={{ fontWeight: 700 }}
              >
                Strengths & Highlights
              </h2>
            </div>
            <div className="flex flex-col gap-3">
              {derived.strengths.map((item, i) => {
                const strength = splitStrength(item);
                return (
                  <div key={`${item}-${i}`} className="flex items-start gap-3">
                    <CheckCircle2
                      className="w-4 h-4 mt-0.5 flex-shrink-0"
                      style={{ color: accentColor }}
                    />
                    <div>
                      <p
                        className="text-[13px] text-slate-800 leading-snug"
                        style={{ fontWeight: 700 }}
                      >
                        {strength.title}
                      </p>
                      {strength.points.length > 0 && (
                        <p
                          className="text-[12px] text-slate-600 leading-relaxed mt-1"
                          style={{ fontWeight: 400 }}
                        >
                          {strength.points.join("; ")}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {derived.courses.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6"
          >
            <div className="flex items-center gap-2 mb-5">
              <BookOpen className="w-4 h-4" style={{ color: accentColor }} />
              <h2
                className="text-[15px] text-slate-900"
                style={{ fontWeight: 700 }}
              >
                Available Courses
              </h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {derived.courses.map((course) => (
                <span
                  key={course}
                  className="text-[12px] text-slate-700 bg-slate-50 border border-slate-100 px-3 py-2 rounded-xl"
                  style={{ fontWeight: 600 }}
                >
                  {course}
                </span>
              ))}
            </div>
          </motion.div>
        )}

        {derived.campus.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-4 h-4" style={{ color: accentColor }} />
              <h2
                className="text-[15px] text-slate-900"
                style={{ fontWeight: 700 }}
              >
                Campus
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {derived.campus.map((campus, i) => (
                <div
                  key={`${campus.location}-${i}`}
                  className="flex items-start gap-2.5"
                >
                  <MapPin
                    className="w-4 h-4 mt-0.5 flex-shrink-0"
                    style={{ color: accentColor }}
                  />
                  <p
                    className="text-[12px] text-slate-600"
                    style={{ fontWeight: 400 }}
                  >
                    {campus.location}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-gradient-to-br from-violet-600 to-purple-700 rounded-3xl p-6 text-white"
        >
          <div className="flex items-center gap-3 mb-4">
            <img src={logo} alt="" className="w-10 h-10" />
            <div>
              <p className="text-[14px]" style={{ fontWeight: 700 }}>
                Ready to apply to {name}?
              </p>
              <p
                className="text-[12px] text-violet-200"
                style={{ fontWeight: 400 }}
              >
                Our counsellor can walk you through the next steps.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            {/* <NavLink
              to="/dashboard/chat"
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-white text-violet-700 text-[13px] hover:bg-violet-50 transition-colors shadow-sm"
              style={{ fontWeight: 700 }}
            >
              <MessageCircle className="w-4 h-4" /> Chat with Counsellor
            </NavLink> */}
            <a
              href={`https://wa.me/601112870256?text=${encodeURIComponent(`Hi SCS Education, I'm interested in ${name}. Can you help me check this university?`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-white/20 hover:bg-white/30 text-white text-[13px] transition-colors"
              style={{ fontWeight: 600 }}
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp Us
            </a>
          </div>
          {uni.website && (
            <a
              href={`https://${uni.website.replace(/^https?:\/\//, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1.5 mt-3 text-[12px] text-violet-300 hover:text-white transition-colors"
              style={{ fontWeight: 500 }}
            >
              <ExternalLink className="w-3.5 h-3.5" /> Visit {uni.website}
            </a>
          )}
        </motion.div>

        <div className="flex items-center justify-between pt-2">
          <NavLink
            to="/explore-universities"
            className="inline-flex items-center gap-2 text-[13px] text-slate-400 hover:text-slate-600 transition-colors"
            style={{ fontWeight: 500 }}
          >
            <ArrowLeft className="w-4 h-4" /> Back to Universities
          </NavLink>
          {/* <NavLink
            to="/courses"
            className="inline-flex items-center gap-2 text-[13px] text-violet-600 hover:text-violet-700 transition-colors"
            style={{ fontWeight: 600 }}
          >
            Explore Courses <ArrowRight className="w-4 h-4" />
          </NavLink> */}
        </div>
      </div>
    </div>
  );
}
